import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useCreditApplicationRedirectUrl from '../../../../Data/Payments/useCreditApplicationRedirectUrl';
import InputCheckbox from '../../../../components/rhf/InputCheckbox';
import InputSelect from '../../../../components/rhf/InputSelect';
import InputText from '../../../../components/rhf/InputText';
import { getMarket } from '../../../../i18n';
import Alert from '../../../../styled-components/components/Alert';
import { Button } from '../../../../styled-components/components/Button';
import { Form, FormGroup } from '../../../../styled-components/components/Form';
import InputError from '../../../../styled-components/components/InputError';
import InputLabel from '../../../../styled-components/components/InputLabel';
import Loader from '../../../../styled-components/components/Loader';
import { StackMedium } from '../../../../styled-components/components/Stack';
import { Text } from '../../../../styled-components/components/Text';
import usePersistCreditApplicationState from './usePersistCreditApplicationState';
import useTrackCreditApplication from './useTrackCreditApplication';
import {
  type FormState,
  FormStateToPayload,
  FormStateToPersisted,
  createOptions,
  defaultState,
  validateTelephonenumber,
} from './utils';

const ns = 'credit-application-vfs';

interface CreditApplicationProps {
  id: string;
  financialInstitution: string;
  downPayment: number;
  duration: number;
  residual?: number;
}

const CreditApplication = ({
  id,
  financialInstitution,
  downPayment,
  duration,
  residual,
}: CreditApplicationProps) => {
  const { marketCode } = getMarket();
  const { t } = useTranslation([ns, 'glossary', 'validation']);
  const [persisted, persistData] = usePersistCreditApplicationState();
  const track = useTrackCreditApplication({
    downPayment,
    duration,
    residual,
    financialInstitution,
  });
  const [createRedirectUrl, requestCreateRedirectUrl, redirectUrl] =
    useCreditApplicationRedirectUrl();

  const loading = requestCreateRedirectUrl.isLoading;

  useEffect(() => {
    track.trackInit();
  }, []);

  useEffect(() => {
    if (redirectUrl) {
      const el = document.createElement('a');
      el.href = redirectUrl;
      el.target = '_blank';
      el.rel = 'noopener noreferrer';
      el.click();
    }
  }, [redirectUrl]);

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = useForm<FormState>({
    defaultValues: defaultState({ id, downPayment, duration, persisted }),
  });

  const onSubmit = handleSubmit((data) => {
    const persistedPayload = FormStateToPersisted(data, marketCode);
    if (persistedPayload) {
      persistData(
        persistedPayload.ssn,
        persistedPayload.email,
        persistedPayload.estimatedMileage,
        persistedPayload.telephone,
      );
    } else {
      persistData();
    }

    const payload = FormStateToPayload(data, marketCode);
    createRedirectUrl(payload);
    track.trackSubmitted();
  });

  const options = useMemo(() => createOptions(marketCode), [marketCode]);

  return (
    <>
      <StackMedium>
        <Text>{t('description', { ns })}</Text>
      </StackMedium>
      <StackMedium>
        <Form>
          <FormGroup>
            <InputLabel>{t('fields.ssn.displayName', { ns })}</InputLabel>
            <InputText
              title={t('fields.ssn.displayName', { ns: 'credit-application-vfs' })}
              register={register('ssn', { required: true })}
              placeholder="ÅÅÅÅMMDD-XXXX"
              disabled={loading}
            />
            {errors.ssn && <InputError>{t('errorWrongFormat', { ns: 'validation' })}</InputError>}
          </FormGroup>
          <FormGroup>
            <InputLabel>{t('fields.estimatedMileage.displayName', { ns })}</InputLabel>
            <InputSelect
              title={t('fields.estimatedMileage.displayName', { ns })}
              register={register('estimatedMileage', { required: true })}
              options={options}
              disabled={loading}
            />
            {errors.estimatedMileage && (
              <InputError>{t('fieldCanNotBeEmpty', { ns: 'validation' })}</InputError>
            )}
          </FormGroup>
          <FormGroup>
            <InputLabel>{t('fields.email.displayName', { ns })}</InputLabel>
            <InputText
              title={t('fields.email.displayName', { ns })}
              register={register('email', { required: true })}
              placeholder={t('emailAddress', { ns: 'glossary' })}
              disabled={loading}
            />
            {errors.email && <InputError>{t('errorWrongFormat', { ns: 'validation' })}</InputError>}
          </FormGroup>
          <FormGroup>
            <InputLabel>{t('fields.telephone.displayName', { ns })}</InputLabel>
            <InputText
              title={t('fields.telephone.displayName', { ns })}
              register={register('telephone', {
                required: true,
                validate: {
                  telephone: (s) => validateTelephonenumber(s, marketCode),
                },
              })}
              placeholder={t('phoneNumber', { ns: 'glossary' })}
              disabled={loading}
            />
            {errors.telephone && (
              <InputError>{t('errorWrongFormatPhoneNumber', { ns: 'validation' })}</InputError>
            )}
          </FormGroup>
          <FormGroup>
            <InputCheckbox
              type="checkbox"
              title={t('fields.persisted.displayName', { ns })}
              register={register('persisted')}
              disabled={loading}
            >
              {t('fields.persisted.displayName', {
                ns,
                brandName: t('brandName', { ns: 'global' }),
              })}
            </InputCheckbox>
          </FormGroup>
          <FormGroup>
            <Button $tone="accent" $fullWidth onClick={onSubmit} disabled={loading}>
              {t('actions.submit', { ns })}
              {loading && <Loader inline />}
            </Button>
          </FormGroup>
        </Form>
      </StackMedium>
      {requestCreateRedirectUrl.isError && (
        <StackMedium>
          <Alert tone="error">{t('alerts.errorCreateRedirectUrl', { ns })}</Alert>
        </StackMedium>
      )}
    </>
  );
};

export default CreditApplication;
